import planck from 'planck-js';
import * as PIXI from 'pixi.js';
import GSObject from '../../Base/GSObject';
import Global from '../../Global';
import Vector from '../../Base/Math/Vector';
import Angle from '../../Base/Math/Angle';
import { CollisionCategory, CollisionGroup } from '../../Collision';
import { Shape } from '.';
import AssetManager from '../../Base/AssetManager';
import '../../../External/pixi-heaven-hue';
import { blokSettings, MovementBehaviour, RotationBehaviour, RotationDirection, ResizeBehaviour, ViewDirection } from '../../../GameKit/shared';
import Image from './Appearance/Image';
import Pattern from './Appearance/Pattern';
import Text from './Appearance/Text';
import LibraryManager from '../../Base/Library/LibraryManager';

export { MovementBehaviour, RotationBehaviour, RotationDirection, ResizeBehaviour };

const AppearanceTypes = [
    // Don't change order
    Image,
    Pattern,
    Text
];

export { AppearanceTypes };

export default class State extends GSObject {
    constructor(gsoParent) {
        super(gsoParent);

        this.blok = gsoParent;

        this.physicsFixture = null;
        this.physicsFixtureSensor = null;
        this.physicsJoint = null;
        this.physicsJointSourceBody = null;

        this.pixiContainer = null;

        this.appearance = new Pattern(this);
        this.appearanceType = AppearanceTypes.indexOf(Pattern);

        // Settings
        this.thingsMoveThroughMe = blokSettings.thingsMoveThroughMe;
        this.friction = blokSettings.friction;
        this.bounce = blokSettings.bounce;
        this.weight = blokSettings.weight;

        // Movement
        this.speed = blokSettings.speed;
        this.movementBehaviour = blokSettings.movementBehaviour;
        this.movementGoal = new Vector(Global.NATIVE_BLOCK_SIZE, Global.NATIVE_BLOCK_SIZE);

        // Rotation
        this.rotationBehaviour = blokSettings.rotationBehaviour;
        this.rotationDirection = blokSettings.rotationDirection;
        this.rotationSpeed = blokSettings.rotationSpeed;
        this.rotationAngle = blokSettings.rotationAngle; // In radians

        // Resize
        this.resizeBehaviour = blokSettings.resizeBehaviour;
        this.resizeSpeed = blokSettings.resizeSpeed;
        this.resizeWidth = blokSettings.resizeWidth; // * blok size
        this.resizeHeight = blokSettings.resizeHeight; // * blok size

        // change back
        this.changesBack = false;
        this.changeBackTime = 0.1;

        // Runtime vars
        this.movementPhase = 0;
        this.rotationPhase = 0;
        this.resizePhase = 0;
        this.startAngle = 0;
        this.startPosition = new Vector(0, 0);
        this.shapeDef = {};
        this.changeBackTimer = 0;
    }

    onCreateVisuals() {
        this.pixiContainer = new PIXI.Container();

        this.blok.pixiStateContainer.addChild(this.pixiContainer);
    }

    onDestroyVisuals() {
        this.pixiContainer.destroy();
    }

    createRevoluteJoint(physicsWorld) {
        this.physicsJointSourceBody = physicsWorld.createBody();
        this.physicsJointSourceBody.setPosition(planck.Vec2(this.blok.physicsBody.getPosition().x, this.blok.physicsBody.getPosition().y));
        if (MovementBehaviour.none) this.physicsJointSourceBody.setStatic();
        else this.physicsJointSourceBody.setKinematic();

        this.physicsJoint = physicsWorld.createJoint(
            planck.RevoluteJoint({}, this.physicsJointSourceBody, this.blok.physicsBody, planck.Vec2(this.blok.physicsBody.getPosition().x, this.blok.physicsBody.getPosition().y))
        );
    }

    onCreateBody(physicsWorld) {
        if (!this.blok._hasBody) throw new Error('The parent Blok does not have a physics presence.');

        if (!physicsWorld) {
            physicsWorld = this.getGame().physicsWorld;
        }

        this.blok.isSensor = false;
        if (this.blok._activeActieReactie) {
            if ((this.blok._activeActieReactie.respondToPlayerCollision || this.blok._activeActieReactie.respondToObjectCollision) && this.thingsMoveThroughMe) this.blok.isSensor = true;
        }
        const shapeDef = { friction: this.friction, density: this.weight, restitution: this.bounce, isSensor: false };
        this.movementPhase = 0;
        this.rotationPhase = 0;
        this.resizePhase = 0;
        this.changeBackTimer = 0;
        this.startAngle = this.blok.physicsBody.getAngle();
        this.startPosition = new Vector(this.getRootBody().getPosition().x * Global.PHYSICS_SCALE, this.getRootBody().getPosition().y * Global.PHYSICS_SCALE);
        this.blok.physicsBody.setFixedRotation(this.rotationBehaviour !== RotationBehaviour.free);

        shapeDef.filterGroupIndex = CollisionGroup.none;
        if (this.movementBehaviour === MovementBehaviour.flee || this.movementBehaviour === MovementBehaviour.approach || this.movementBehaviour === MovementBehaviour.free) {
            this.blok.physicsBody.setDynamic();
            this.blok.physicsBody.setAwake(true);
            shapeDef.filterCategoryBits = CollisionCategory.dynamic;
        } else if (this.movementBehaviour === MovementBehaviour.to || this.movementBehaviour === MovementBehaviour.backAndForth) {
            if (this.rotationBehaviour === RotationBehaviour.free) {
                this.blok.physicsBody.setDynamic();
                this.createRevoluteJoint(physicsWorld);
            } else {
                this.blok.physicsBody.setKinematic();
            }
            shapeDef.filterCategoryBits = CollisionCategory.static;
        } else {
            if (this.rotationBehaviour === RotationBehaviour.free) {
                this.blok.physicsBody.setDynamic();
                this.createRevoluteJoint(physicsWorld);
            } else if (this.rotationBehaviour === RotationBehaviour.once || this.rotationBehaviour === RotationBehaviour.backAndForth || this.rotationBehaviour === RotationBehaviour.continous) {
                this.blok.physicsBody.setKinematic();
            } else {
                this.blok.physicsBody.setStatic();
            }
            shapeDef.filterCategoryBits = CollisionCategory.static;
        }
        if (this.blok.isSensor || this.thingsMoveThroughMe) {
            if (this.movementBehaviour === MovementBehaviour.flee || this.movementBehaviour === MovementBehaviour.approach || this.movementBehaviour === MovementBehaviour.free) {
                shapeDef.filterMaskBits = CollisionCategory.static;
            } else {
                shapeDef.filterCategoryBits = CollisionCategory.none;
                shapeDef.filterMaskBits = CollisionCategory.none;
            }
        } else {
            shapeDef.filterMaskBits = CollisionCategory.dynamic + CollisionCategory.static;
        }
        this.shapeDef = shapeDef;
        this.physicsFixture = this.blok.physicsBody.createFixture(this.blok.physicsShape, shapeDef);
        if (this.blok.level.viewDirection === ViewDirection.TopDown) {
            this.blok.physicsBody.setLinearDamping(5 * this.friction);
            this.blok.physicsBody.setAngularDamping(5 * this.friction);
        }
        if (this.blok.isSensor) {
            shapeDef.isSensor = true;
            shapeDef.filterCategoryBits = CollisionCategory.static;
            shapeDef.filterMaskBits = CollisionCategory.dynamic;
            this.physicsFixtureSensor = this.blok.physicsBody.createFixture(this.blok.physicsShape, shapeDef);
        }
    }

    onDestroyBody(physicsWorld) {
        if (!this.blok._hasBody) throw new Error('The parent Blok does not have a physics presence.');
        this.blok.physicsBody.destroyFixture(this.physicsFixture);
        if (this.physicsFixtureSensor) this.blok.physicsBody.destroyFixture(this.physicsFixtureSensor);
        if (this.physicsJoint) {
            physicsWorld.destroyJoint(this.physicsJoint);
            physicsWorld.destroyBody(this.physicsJointSourceBody);
            this.physicsJointSourceBody = null;
            this.physicsJoint = null;
        }
        this.physicsFixtureSensor = null;
        this.physicsFixture = null;
        this.physicsJoint = null;
    }

    getRootBody() {
        if (this.physicsJointSourceBody) return this.physicsJointSourceBody;
        return this.blok.physicsBody;
    }

    onFixedUpdate(deltaTime) {
        if (this.blok._hasBody) {
            if (this.changesBack) {
                this.changeBackTimer += deltaTime / 1000;
                if (this.changeBackTimer >= this.changeBackTime) {
                    this.blok.setActiveState(0);
                    this.changeBackTimer = 0;
                }
            }

            if (this.movementBehaviour === MovementBehaviour.to || this.movementBehaviour === MovementBehaviour.backAndForth) {
                const bodyPosition = new Vector(this.getRootBody().getPosition().x * Global.PHYSICS_SCALE, this.getRootBody().getPosition().y * Global.PHYSICS_SCALE);
                const startPosition = bodyPosition.copy();
                let goal = this.movementGoal.add(this.startPosition);
                switch (this.movementBehaviour) {
                    case MovementBehaviour.to:
                        bodyPosition.moveTo(goal, this.speed * deltaTime * 0.02);
                        break;
                    case MovementBehaviour.backAndForth:
                        if (this.movementPhase === 1) goal = this.startPosition;
                        if (bodyPosition.moveTo(goal, this.speed * deltaTime * 0.02)) {
                            if (this.movementPhase === 1) this.movementPhase = 0;
                            else this.movementPhase = 1;
                        }
                        break;
                    default:
                        break;
                }

                this.getRootBody().setLinearVelocity(
                    bodyPosition
                        .sub(startPosition)
                        .mul(new Vector(deltaTime))
                        .div(new Vector(Global.PHYSICS_SCALE))
                        .makePlanckVec2()
                );
            } else if (this.movementBehaviour === MovementBehaviour.flee || this.movementBehaviour === MovementBehaviour.approach) {
                const bodyPosition = new Vector(this.getRootBody().getPosition().x * Global.PHYSICS_SCALE, this.getRootBody().getPosition().y * Global.PHYSICS_SCALE);
                const playerPosition = new Vector(
                    this._gsoParent._gsoParent.player.physicsBody.getPosition().x * Global.PHYSICS_SCALE,
                    this._gsoParent._gsoParent.player.physicsBody.getPosition().y * Global.PHYSICS_SCALE
                );
                const direction = bodyPosition.sub(playerPosition);
                direction.normalize();
                if (this.movementBehaviour === MovementBehaviour.flee) {
                    direction.mulm(new Vector(this.speed * 0.02 * deltaTime * Global.PHYSICS_SCALE_OG_FACTOR * this.blok.getWeight()));
                } else if (this.movementBehaviour === MovementBehaviour.approach) {
                    direction.mulm(new Vector(this.speed * -0.02 * deltaTime * Global.PHYSICS_SCALE_OG_FACTOR * this.blok.getWeight()));
                }
                this.getRootBody().applyLinearImpulse(direction.makePlanckVec2(), this.getRootBody().getPosition(), true);
            }
            if (this.movementBehaviour === MovementBehaviour.none) {
                this.getRootBody().setLinearVelocity(new Vector(0).makePlanckVec2());
            }

            if (this.rotationBehaviour === RotationBehaviour.continous) {
                let direction = 1;
                if (this.rotationDirection === RotationDirection.left) direction = -1;
                this.blok.physicsBody.setAngularVelocity(this.rotationSpeed * 0.1 * direction);
            }
            if (this.rotationBehaviour === RotationBehaviour.once || this.rotationBehaviour === RotationBehaviour.backAndForth) {
                if (this.rotationPhase === 0 || this.rotationBehaviour === RotationBehaviour.backAndForth) {
                    let direction = 1;
                    let left = 1;
                    let right = 0;
                    if (this.rotationDirection === RotationDirection.left) {
                        direction = -1;
                        left = 0;
                        right = 1;
                    }
                    const originalDirection = direction;
                    let goal = this.startAngle + this.rotationAngle * originalDirection;
                    if (this.rotationBehaviour === RotationBehaviour.backAndForth && this.rotationPhase === 1) {
                        direction = -direction;
                        const t = left;
                        left = right;
                        right = t;
                        goal = this.startAngle;
                    }

                    const stepVelocity = this.rotationSpeed * 0.0001 * deltaTime;
                    const currentAngle = this.blok.physicsBody.getAngle();
                    if (Angle.isInRange(currentAngle, goal, stepVelocity * left * 1.00001, stepVelocity * right * 1.00001)) {
                        this.blok.physicsBody.setAngularVelocity(0);
                        if (this.rotationBehaviour === RotationBehaviour.once) {
                            this.rotationPhase = 1;
                            this.blok.physicsBody.setAngle(goal);
                        } else if (this.rotationBehaviour === RotationBehaviour.backAndForth) {
                            if (this.rotationPhase === 0) this.rotationPhase = 1;
                            else this.rotationPhase = 0;
                        }
                    } else {
                        this.blok.physicsBody.setAngularVelocity(this.rotationSpeed * 0.1 * direction);
                    }
                }
            }
            if (this.rotationBehaviour === RotationBehaviour.none) {
                this.blok.physicsBody.setAngularVelocity(0);
            }

            if (this.resizeBehaviour === ResizeBehaviour.once || this.resizeBehaviour === ResizeBehaviour.backAndForth) {
                const scale = this.blok.nextScale.copy();
                let goal = new Vector(Number(this.resizeWidth), Number(this.resizeHeight));
                switch (this.resizeBehaviour) {
                    case ResizeBehaviour.once:
                        scale.moveTo(goal, (this.resizeSpeed / 100) * deltaTime * 0.1);
                        break;
                    case ResizeBehaviour.backAndForth:
                        if (this.resizePhase === 1) goal = new Vector(1, 1);
                        if (scale.moveTo(goal, (this.resizeSpeed / 100) * deltaTime * 0.1)) {
                            if (this.resizePhase === 1) this.resizePhase = 0;
                            else this.resizePhase = 1;
                        }
                        break;
                    default:
                        break;
                }
                this.blok.nextScale = scale.copy();
            }
        }
    }

    serialize() {
        const data = this.autoSerialize();
        data.appearance = this.appearance.serialize();
        if (data.appearanceType === undefined || data.appearanceType === AppearanceTypes.indexOf(Image) || data.appearanceType === AppearanceTypes.indexOf(Pattern)) {
            if (!data.appearance.texture) {
                data.appearance.texture = 'wall';
            }
        }
        return data;
    }

    deserialize(data) {
        this.autoDeserialize(data);
        if (data.appearanceType === undefined || data.appearanceType === AppearanceTypes.indexOf(Image) || data.appearanceType === AppearanceTypes.indexOf(Pattern)) {
            if (data.appearance.texture) {
                if (LibraryManager.textures.get(data.appearance.texture).stretch) data.appearanceType = AppearanceTypes.indexOf(Image);
                else data.appearanceType = AppearanceTypes.indexOf(Pattern);
            } else {
                data.appearanceType = AppearanceTypes.indexOf(Image);
            }
        }
        this.appearance.destroy();
        if (data.appearanceType !== AppearanceTypes.indexOf(Text)) {
            if (data.appearance.texture === '') {
                data.appearanceType = AppearanceTypes.indexOf(Image);
            }
        }
        this.appearance = new AppearanceTypes[data.appearanceType](this);
        this.appearanceType = data.appearanceType;
        this.appearance.deserialize(data.appearance);
    }
}

State.serializeableFields = {
    thingsMoveThroughMe: Boolean,
    friction: Number,
    bounce: Number,
    weight: Number,
    speed: Number,
    movementBehaviour: Number,
    movementGoal: Vector,
    rotationBehaviour: Number,
    rotationDirection: Number,
    rotationSpeed: Number,
    rotationAngle: Angle,
    resizeBehaviour: Number,
    resizeSpeed: Number,
    resizeWidth: Number,
    resizeHeight: Number,
    appearanceType: Number,
    changesBack: Number,
    changeBackTime: Number
};
